/**
 * International (Philippines) application - single source of truth for the
 * wizard steps, per-step field whitelists, option lists, audio-assessment
 * scripts, and consent copy.
 *
 * Client-safe: no DB access. The public step-save route builds its UPDATE
 * exclusively from STEP_FIELDS (no column injection); the wizard renders
 * from the same definitions so the two can't drift.
 *
 * Content (option lists, scripts, consent paragraphs) transcribed from HR's
 * "Reclutamiento en Filipinas" form spec. The computer equipment and backup
 * resources questions (2026-10-22) follow HR's "TELI Application Form
 * Updates" and share their option lists with the Marketing form
 * (equipment-backup-options.ts).
 */
import { conditionHolds, type FormCondition, type FormValues } from '@/lib/recruitment/intl-form-engine';
import {
  BACKUP_INTERNET_NONE,
  BACKUP_INTERNET_OPTIONS,
  BACKUP_INTERNET_OTHER,
  BACKUP_POWER_EQUIPMENT_OPTIONS,
  BACKUP_POWER_NONE,
  BACKUP_POWER_OPTIONS,
  BACKUP_POWER_OTHER,
  BACKUP_POWER_SOURCES,
  COMPUTER_TYPE_LABELS,
  COMPUTER_TYPE_OPTIONS,
  DEVICE_LABELS,
  DEVICE_OPTIONS,
  SPEC_UNKNOWN,
  STORAGE_TYPE_OPTIONS,
  YES_NO_OPTIONS,
  YES_NO_UNKNOWN_OPTIONS,
  backupPowerEquipmentField,
  backupPowerRuntimeField,
} from '@/lib/recruitment/equipment-backup-options';

export const INTL_STEPS = [
  'personal',
  'identity',
  'english',
  'audio',
  'work',
  'equipment',
  'availability',
  'expectations',
  'documents',
  'typing',
  'consent',
] as const;

export type IntlStepKey = (typeof INTL_STEPS)[number];

export const INTL_STEP_TITLES: Record<IntlStepKey, string> = {
  personal: 'Personal Information',
  identity: 'Identity Verification',
  english: 'English Proficiency',
  audio: 'Audio Assessment',
  work: 'Work Experience',
  equipment: 'Home Office Equipment',
  availability: 'Availability',
  expectations: 'Expectations',
  documents: 'Documents',
  typing: 'Typing Test',
  consent: 'Consent & Authorization',
};

/** Steps whose data is saved through the step-save endpoint (form fields). */
export const INTL_DATA_STEPS: IntlStepKey[] = [
  'personal',
  'english',
  'work',
  'equipment',
  'availability',
  'expectations',
];

/** Steps completed by their own endpoints, not the step-save route. */
export const INTL_UPLOAD_STEPS: IntlStepKey[] = ['identity', 'audio', 'documents', 'typing'];

export type IntlFieldType =
  | 'string'
  | 'text'
  | 'bool'
  | 'number'
  | 'date'
  | 'string_array'
  /** Hours and minutes, stored as whole minutes (battery life, backup runtime). */
  | 'duration';

export type IntlFieldDef = {
  /** DB column on recruitment_intl_applications AND payload key. */
  column: string;
  /**
   * 'extra' = not a column: the answer lives in the extra_answers JSONB under
   * `column`, the same key the Marketing form uses for the same question.
   */
  storage?: 'extra';
  label: string;
  type: IntlFieldType;
  required?: boolean;
  maxLen?: number;
  /** For type 'number'. */
  min?: number;
  max?: number;
  /** Allowed values for enum-like strings / array items. */
  options?: readonly string[];
  /** Display labels for stored option values ('Desktop' -> 'Desktop computer'). */
  optionLabels?: Record<string, string>;
  /** string_array: this option cannot be combined with the others ("I do not have..."). */
  exclusiveOption?: string;
  /** Free-text companion column revealed when 'Other' is selected. */
  otherColumn?: string;
  /** Only relevant/required when this other boolean column is true. */
  dependsOn?: string;
  /** With dependsOn: shown with its parent but may be left empty. */
  optional?: boolean;
  /**
   * Shown (and saved) only while the condition holds on the other answers of
   * the step (keyed by column); hidden answers are stored as NULL. Unlike
   * dependsOn, a hidden field is required only when `required` says so.
   */
  showWhen?: FormCondition;
  /** Free-text string fields: a tick box that answers with this text ("I do not know"). */
  unknownOption?: string;
  /** Small block heading rendered above the question (first question of a block). */
  heading?: string;
  help?: string;
  placeholder?: string;
};

const NO_FILES: ReadonlySet<string> = new Set();

/**
 * Whether a question is asked, given the other answers of its step (the
 * wizard state, the coerced payload or the stored record, keyed by column).
 */
export function intlFieldShown(def: IntlFieldDef, values: FormValues): boolean {
  if (def.dependsOn && values[def.dependsOn] !== true) return false;
  return !def.showWhen || conditionHolds(def.showWhen, values, NO_FILES);
}

/** Answer of a field in an application record: its column, else its key in extra_answers. */
export function intlStoredValue(
  def: IntlFieldDef,
  record: Record<string, unknown> | null | undefined
): unknown {
  if (!record) return null;
  if (def.storage !== 'extra') return record[def.column];
  const extra = record.extra_answers;
  return extra && typeof extra === 'object' && !Array.isArray(extra)
    ? (extra as Record<string, unknown>)[def.column]
    : null;
}

export const GENDER_OPTIONS = ['Male', 'Female', 'Prefer not to say'] as const;

export const ENGLISH_LEVEL_OPTIONS = [
  'Intermediate',
  'Upper Intermediate',
  'Advanced',
  'Native / Near Native',
] as const;

export const SERVICE_CATEGORY_OPTIONS = [
  'Customer Service',
  'Technical Support',
  'Sales',
  'Medical',
  'Insurance',
  'Collections',
  'Dispatch',
  'Appointment Setting',
  'Back Office',
  'Live Chat Support',
  'Email Support',
  'Healthcare',
  'Billing',
  'Retention',
  'Other',
] as const;

export const ENGLISH_USAGE_OPTIONS = [
  'Every day',
  'Most of the time',
  'Occasionally',
  'Rarely',
] as const;

export const ACCENT_OPTIONS = [
  'American English',
  'British English',
  'Australian English',
  'Neutral / International English',
  'Other',
] as const;

export const CAMPAIGN_TYPE_OPTIONS = [
  'Inbound',
  'Outbound',
  'B2B',
  'B2C',
  'Collections',
  'Medical',
  'Insurance',
  'Healthcare',
  'Legal',
  'Dispatch',
  'Sales',
  'Customer Service',
  'Chat Support',
  'Email Support',
  'Voice Support',
] as const;

export const TOOL_OPTIONS = [
  'HubSpot',
  'Zoho',
  'Slack',
  'Microsoft Teams',
  'Google Workspace',
  'Microsoft Office',
  'Other',
] as const;

export const OS_OPTIONS = ['Windows', 'macOS'] as const;

export const RAM_OPTIONS = ['4 GB', '8 GB', '16 GB', '32 GB or more', "I don't know"] as const;

/**
 * Processor options of the form before 2026-10-22. The question is now free
 * text (full model and generation, "I do not know" tick box) so the IT rule
 * can read the model number; stored answers of older applications still show
 * these values.
 */
export const LEGACY_PROCESSOR_OPTIONS = [
  'Intel Core i3',
  'Intel Core i5',
  'Intel Core i7',
  'AMD Ryzen',
  "I don't know",
] as const;

/** Conditions of the equipment step (keyed by column). */
const BOTH_COMPUTERS: FormCondition = { field: 'computer_type', equals: 'Both' };
const HAS_LAPTOP: FormCondition = { field: 'computer_type', in: ['Laptop', 'Both'] };
const HAS_BACKUP_INTERNET: FormCondition = { field: 'backup_internet_options', anyExcept: BACKUP_INTERNET_NONE };

const PROCESSOR_PLACEHOLDER = 'e.g. Intel Core i5-1135G7, AMD Ryzen 5 5500U';

/** The equipment the applicant's backup power keeps running and for how long, per source. */
const BACKUP_POWER_SOURCE_FIELDS: IntlFieldDef[] = BACKUP_POWER_SOURCES.flatMap((source) => [
  {
    column: backupPowerEquipmentField(source.key),
    storage: 'extra',
    label: `${source.short}: which equipment can it keep running?`,
    type: 'string',
    required: true,
    options: BACKUP_POWER_EQUIPMENT_OPTIONS,
    showWhen: { field: 'backup_power_sources', includes: source.option },
  },
  {
    column: backupPowerRuntimeField(source.key),
    storage: 'extra',
    label: `${source.short}: approximately how long can it keep that equipment running?`,
    type: 'duration',
    required: true,
    showWhen: { field: 'backup_power_sources', includes: source.option },
  },
]);

export const NOISE_LEVEL_OPTIONS = ['Never', 'Low', 'Moderate', 'High'] as const;

// Same options as the Marketing form (marketing-application.ts).
export const HEADSET_CONNECTION_OPTIONS = ['USB', '3.5 mm', 'Bluetooth', 'Other'] as const;

export const INTERNET_CONNECTION_OPTIONS = ['Ethernet', 'Wi-Fi', 'Other'] as const;

export const START_AVAILABILITY_OPTIONS = ['1_week', '2_weeks', '30_days'] as const;

export const START_AVAILABILITY_LABELS: Record<string, string> = {
  '1_week': 'Within 1 week',
  '2_weeks': 'Within 2 weeks',
  '30_days': 'Within 30 days',
};

export const STEP_FIELDS: Record<string, IntlFieldDef[]> = {
  personal: [
    { column: 'full_name', label: 'Full Name', type: 'string', required: true, maxLen: 255 },
    { column: 'date_of_birth', label: 'Date of Birth', type: 'date', required: true },
    { column: 'age', label: 'Age', type: 'number', min: 15, max: 100 },
    { column: 'gender', label: 'Gender', type: 'string', required: true, options: GENDER_OPTIONS },
    { column: 'email', label: 'Email Address', type: 'string', required: true, maxLen: 255 },
    { column: 'mobile_number', label: 'Mobile Phone Number', type: 'string', required: true, maxLen: 50 },
    { column: 'whatsapp_number', label: 'WhatsApp Number', type: 'string', required: true, maxLen: 50 },
    { column: 'facebook_url', label: 'Facebook Profile (Optional)', type: 'string', maxLen: 500 },
    { column: 'linkedin_url', label: 'LinkedIn Profile (Optional)', type: 'string', maxLen: 500 },
    { column: 'city', label: 'City', type: 'string', required: true, maxLen: 100 },
    { column: 'state_province', label: 'State / Province', type: 'string', required: true, maxLen: 100 },
    { column: 'postal_code', label: 'Postal Code / ZIP Code', type: 'string', required: true, maxLen: 20 },
    { column: 'country', label: 'Country', type: 'string', required: true, maxLen: 100 },
  ],
  english: [
    { column: 'english_level', label: 'English proficiency', type: 'string', required: true, options: ENGLISH_LEVEL_OPTIONS },
    { column: 'has_english_experience', label: 'Worked in an English-speaking environment', type: 'bool', required: true },
    { column: 'english_experience_years', label: 'Years of English-speaking experience', type: 'number', min: 0, max: 60, dependsOn: 'has_english_experience' },
    { column: 'service_categories', label: 'Customer support / services provided', type: 'string_array', required: true, options: SERVICE_CATEGORY_OPTIONS, otherColumn: 'service_categories_other' },
    { column: 'service_categories_other', label: 'Other services', type: 'text', maxLen: 300 },
    { column: 'english_usage_frequency', label: 'How often you communicated with customers in English', type: 'string', required: true, options: ENGLISH_USAGE_OPTIONS },
    { column: 'preferred_accent', label: 'Most comfortable English accent', type: 'string', required: true, options: ACCENT_OPTIONS, otherColumn: 'preferred_accent_other' },
    { column: 'preferred_accent_other', label: 'Other accent', type: 'text', maxLen: 100 },
  ],
  work: [
    { column: 'last_company', label: 'Company Name', type: 'string', required: true, maxLen: 255 },
    { column: 'last_job_title', label: 'Job Title', type: 'string', required: true, maxLen: 255 },
    { column: 'work_start_date', label: 'Employment Start Date', type: 'date', required: true },
    { column: 'work_end_date', label: 'Employment End Date', type: 'date' },
    { column: 'currently_employed', label: 'Currently employed at this company', type: 'bool', required: true },
    { column: 'reason_for_leaving', label: 'Reason for Leaving', type: 'text', maxLen: 1000 },
    { column: 'campaign_types', label: 'Campaign type(s) worked on', type: 'string_array', required: true, options: CAMPAIGN_TYPE_OPTIONS },
    { column: 'crms_used', label: 'CRM(s) used', type: 'text', maxLen: 500 },
    { column: 'tools_used', label: 'Tools and software', type: 'string_array', required: true, options: TOOL_OPTIONS, otherColumn: 'tools_other' },
    { column: 'tools_other', label: 'Other tools', type: 'text', maxLen: 300 },
  ],
  // Computer equipment and backup resources per HR's 2026-10-22 update: the
  // existing columns describe the (primary) computer the IT rules evaluate;
  // a second "backup_*" block appears when the applicant will use both a
  // desktop and a laptop. Backup power and backup internet close the step.
  equipment: [
    { column: 'owns_computer', label: 'Do you have your own computer?', type: 'bool', required: true },
    { column: 'computer_type', storage: 'extra', label: 'What type of computer will you use to work with TELI?', type: 'string', required: true, options: COMPUTER_TYPE_OPTIONS, optionLabels: COMPUTER_TYPE_LABELS, help: 'Select one option. If you select Both, you will describe each computer separately.' },
    { column: 'primary_device', storage: 'extra', label: 'Which computer will be your primary device?', type: 'string', required: true, options: DEVICE_OPTIONS, optionLabels: DEVICE_LABELS, showWhen: BOTH_COMPUTERS, help: 'The other computer will be your backup device.' },
    { column: 'computer_brand_model', storage: 'extra', heading: 'Computer specifications', label: 'Brand and model', type: 'string', required: true, maxLen: 200, unknownOption: SPEC_UNKNOWN, placeholder: 'e.g. Lenovo IdeaPad 3, Dell OptiPlex 3080', help: 'Your primary computer if you will use both a desktop and a laptop.' },
    { column: 'computer_os', label: 'Operating system', type: 'string', required: true, options: OS_OPTIONS },
    { column: 'os_version', storage: 'extra', label: 'Operating system version (if known)', type: 'string', maxLen: 100, placeholder: 'e.g. Windows 11' },
    { column: 'computer_processor', label: 'Processor: full model and generation, if known', type: 'string', required: true, maxLen: 150, unknownOption: SPEC_UNKNOWN, placeholder: PROCESSOR_PLACEHOLDER },
    { column: 'computer_ram', label: 'Installed RAM', type: 'string', required: true, options: RAM_OPTIONS },
    { column: 'storage_type', storage: 'extra', label: 'Storage type', type: 'string', required: true, options: STORAGE_TYPE_OPTIONS },
    { column: 'free_storage_gb', storage: 'extra', label: 'Available free storage in GB', type: 'number', required: true, min: 0, max: 100000 },
    // The specs screenshot upload card renders here in the wizard (slot after free_storage_gb).
    { column: 'backup_computer_brand_model', storage: 'extra', heading: 'Backup device specifications', label: 'Brand and model', type: 'string', required: true, maxLen: 200, unknownOption: SPEC_UNKNOWN, showWhen: BOTH_COMPUTERS, help: 'The computer you will use as backup.' },
    { column: 'backup_computer_os', storage: 'extra', label: 'Operating system', type: 'string', required: true, options: OS_OPTIONS, showWhen: BOTH_COMPUTERS },
    { column: 'backup_os_version', storage: 'extra', label: 'Operating system version (if known)', type: 'string', maxLen: 100, placeholder: 'e.g. Windows 11', showWhen: BOTH_COMPUTERS },
    { column: 'backup_computer_processor', storage: 'extra', label: 'Processor: full model and generation, if known', type: 'string', required: true, maxLen: 150, unknownOption: SPEC_UNKNOWN, placeholder: PROCESSOR_PLACEHOLDER, showWhen: BOTH_COMPUTERS },
    { column: 'backup_computer_ram', storage: 'extra', label: 'Installed RAM', type: 'string', required: true, options: RAM_OPTIONS, showWhen: BOTH_COMPUTERS },
    { column: 'backup_storage_type', storage: 'extra', label: 'Storage type', type: 'string', required: true, options: STORAGE_TYPE_OPTIONS, showWhen: BOTH_COMPUTERS },
    { column: 'backup_free_storage_gb', storage: 'extra', label: 'Available free storage in GB', type: 'number', required: true, min: 0, max: 100000, showWhen: BOTH_COMPUTERS },
    // The backup specs screenshot upload card renders here in the wizard (slot after backup_free_storage_gb).
    { column: 'laptop_unplugged', storage: 'extra', label: 'Does your laptop work without being plugged into a power outlet?', type: 'bool', required: true, showWhen: HAS_LAPTOP },
    { column: 'laptop_battery_duration', storage: 'extra', label: 'Approximately how long does its battery last during normal work use?', type: 'duration', required: true, showWhen: { field: 'laptop_unplugged', equals: true } },
    { column: 'isp_provider', heading: 'Internet connection', label: 'Internet Service Provider', type: 'string', required: true, maxLen: 150 },
    { column: 'download_mbps', label: 'Download Speed (Mbps)', type: 'number', required: true, min: 0, max: 10000 },
    { column: 'upload_mbps', label: 'Upload Speed (Mbps)', type: 'number', required: true, min: 0, max: 10000 },
    { column: 'connection_type', storage: 'extra', label: 'Connection you will use', type: 'string', required: true, options: INTERNET_CONNECTION_OPTIONS },
    // The speedtest screenshot upload card renders here in the wizard (slot after connection_type).
    { column: 'has_webcam', heading: 'Peripherals and workspace', label: 'Do you have a webcam?', type: 'bool', required: true },
    { column: 'has_usb_headset', label: 'Do you have a functioning headset with microphone?', type: 'bool', required: true },
    { column: 'headset_connection', storage: 'extra', label: 'Headset connection type', type: 'string', options: HEADSET_CONNECTION_OPTIONS, dependsOn: 'has_usb_headset' },
    { column: 'headset_brand_model', storage: 'extra', label: 'Headset brand and model (Optional)', type: 'string', maxLen: 200, dependsOn: 'has_usb_headset', optional: true },
    { column: 'has_quiet_workspace', label: 'Do you have a quiet and private workspace?', type: 'bool', required: true },
    { column: 'workspace_noise_level', label: 'Noise level in your workspace', type: 'string', required: true, options: NOISE_LEVEL_OPTIONS },
    { column: 'backup_power_sources', storage: 'extra', heading: 'Backup power', label: 'Do you have a backup power source that allows you to continue working during a power outage?', type: 'string_array', required: true, options: BACKUP_POWER_OPTIONS, exclusiveOption: BACKUP_POWER_NONE, help: 'Select all that apply.' },
    { column: 'backup_power_other_system', storage: 'extra', label: 'Which other backup power system?', type: 'string', required: true, maxLen: 200, showWhen: { field: 'backup_power_sources', includes: BACKUP_POWER_OTHER } },
    ...BACKUP_POWER_SOURCE_FIELDS,
    { column: 'backup_internet_options', storage: 'extra', heading: 'Backup internet', label: 'Do you have a backup internet connection ready to use if your main connection fails?', type: 'string_array', required: true, options: BACKUP_INTERNET_OPTIONS, exclusiveOption: BACKUP_INTERNET_NONE, help: 'Select all that apply.' },
    { column: 'backup_internet_other_option', storage: 'extra', label: 'Which other backup internet option?', type: 'string', required: true, maxLen: 200, showWhen: { field: 'backup_internet_options', includes: BACKUP_INTERNET_OTHER } },
    { column: 'backup_internet_provider', storage: 'extra', label: 'Backup internet provider name', type: 'string', required: true, maxLen: 150, showWhen: HAS_BACKUP_INTERNET },
    { column: 'backup_internet_different_provider', storage: 'extra', label: 'Does it use a different provider or network from your main internet connection?', type: 'string', required: true, options: YES_NO_UNKNOWN_OPTIONS, showWhen: HAS_BACKUP_INTERNET },
    { column: 'backup_internet_active_plan', storage: 'extra', label: 'Does the service currently have an active plan or available credit/data so it can be used immediately?', type: 'string', required: true, options: YES_NO_OPTIONS, showWhen: HAS_BACKUP_INTERNET },
    { column: 'backup_internet_prepaid_plan', storage: 'extra', label: 'If it is prepaid, how do you make sure credit or data is available when an interruption occurs?', type: 'text', maxLen: 1000, showWhen: HAS_BACKUP_INTERNET, help: 'Short written explanation. Leave it empty if the service is not prepaid.' },
  ],
  availability: [
    { column: 'works_weekends', label: 'Available to work weekends', type: 'bool', required: true },
    { column: 'start_availability', label: 'When would you be available to start?', type: 'string', required: true, options: START_AVAILABILITY_OPTIONS },
  ],
  expectations: [
    { column: 'expected_salary_php', label: 'Expected monthly salary (PHP)', type: 'number', required: true, min: 0, max: 10000000 },
    { column: 'accepts_independent_contractor', label: 'Willing to work as an Independent Contractor', type: 'bool', required: true },
    { column: 'has_us_mx_company_experience', label: 'Experience with US/Mexico-based companies', type: 'bool', required: true },
    { column: 'has_remote_experience', label: 'Previously worked in a remote position', type: 'bool', required: true },
    { column: 'remote_experience_years', label: 'Years of remote work experience', type: 'number', min: 0, max: 60, dependsOn: 'has_remote_experience' },
  ],
  // Read-only in the HR modal: the typing step saves through its own endpoint
  // (server-computed), never the step-save route - 'typing' is NOT a data step.
  typing: [
    { column: 'typing_wpm', label: 'Words Per Minute (net)', type: 'number' },
    { column: 'typing_accuracy', label: 'Accuracy (%)', type: 'number' },
    { column: 'typing_attempts', label: 'Attempts (max 2)', type: 'number' },
  ],
};

// ---------------------------------------------------------------------------
// Files
// ---------------------------------------------------------------------------

export const INTL_FILE_KINDS = [
  'gov_id',
  'selfie_with_id',
  'specs',
  'specs_backup',
  'speedtest',
  'resume',
] as const;
export type IntlFileKind = (typeof INTL_FILE_KINDS)[number];

export const INTL_FILE_LABELS: Record<IntlFileKind, string> = {
  gov_id: 'Government-Issued ID',
  selfie_with_id: 'Selfie Holding Your Government ID',
  specs: 'Computer Specifications Screenshot',
  specs_backup: 'Backup Device Specifications Screenshot',
  speedtest: 'Speedtest Screenshot',
  resume: 'Resume / CV',
};

/** Which wizard step each upload kind belongs to (completion tracking). */
export const INTL_FILE_STEP: Record<IntlFileKind, IntlStepKey> = {
  gov_id: 'identity',
  selfie_with_id: 'identity',
  specs: 'equipment',
  specs_backup: 'equipment',
  speedtest: 'equipment',
  resume: 'documents',
};

/** Upload kinds that take a PDF as well as an image (screenshots of the specs). */
export const INTL_IMAGE_PDF_KINDS: readonly IntlFileKind[] = ['specs', 'specs_backup'];

/**
 * Uploads the application must have to be submitted: every kind, except the
 * backup device screenshot, which is only asked when the applicant will use
 * both a desktop and a laptop.
 */
export function intlRequiredFileKinds(
  record: Record<string, unknown> | null | undefined,
): IntlFileKind[] {
  const extra =
    record?.extra_answers && typeof record.extra_answers === 'object' && !Array.isArray(record.extra_answers)
      ? (record.extra_answers as Record<string, unknown>)
      : {};
  const both = extra.computer_type === 'Both';
  return INTL_FILE_KINDS.filter((kind) => kind !== 'specs_backup' || both);
}

export const INTL_FILE_MAX_BYTES = 10 * 1024 * 1024; // 10 MB per file

// ---------------------------------------------------------------------------
// Audio assessment
// ---------------------------------------------------------------------------

export const INTL_AUDIO_PARTS = [
  {
    key: 'script_reading',
    title: 'Part 1 - Read the following script',
    instructions: 'Read the script below naturally and clearly, as if you were on a live call.',
    scriptText: `Thank you for calling. My name is Alex, and I'd be happy to help you today.

I completely understand that you'd like to know the price before scheduling the service. Every job is a little different, so our technician first needs to know your location and the type of service you need.

Once we have your address, we'll check which technician is closest to you and provide the most accurate estimate possible.

Could you please share the service address so I can assist you further?`,
    minSeconds: 5,
    maxSeconds: 120,
  },
  {
    key: 'objection_handling',
    title: 'Part 2 - Customer Objection',
    instructions: 'Please read the following response naturally.',
    scriptText: `I completely understand where you're coming from, and I would probably ask the same question.

The reason we request the address first is because pricing depends on several factors, including your location, the type of service, and technician availability.

Once I have your address, I can connect you with the nearest technician, who will gladly answer any questions before any work begins.

Would you mind sharing the address where the service is needed?`,
    minSeconds: 5,
    maxSeconds: 120,
  },
  {
    key: 'self_introduction',
    title: 'Part 3 - Introduce Yourself (No Reading)',
    instructions:
      'Now, without reading, answer in your own words: "Tell us about yourself, your work experience, and why you would like to join TELI." Speak for 45–60 seconds.',
    scriptText: null,
    minSeconds: 30,
    maxSeconds: 120,
  },
] as const;

export type IntlAudioPartKey = (typeof INTL_AUDIO_PARTS)[number]['key'];

export const INTL_AUDIO_PART_KEYS = INTL_AUDIO_PARTS.map((p) => p.key) as IntlAudioPartKey[];

export const INTL_AUDIO_PART_LABELS: Record<IntlAudioPartKey, string> = {
  script_reading: 'Part 1 - Script Reading',
  objection_handling: 'Part 2 - Customer Objection',
  self_introduction: 'Part 3 - Self Introduction',
};

/** Hard cap enforced client-side (auto-stop) and server-side (duration check). */
export const INTL_AUDIO_MAX_SECONDS = 120;
export const INTL_AUDIO_MAX_BYTES = 25 * 1024 * 1024; // 25 MB (convention)

export const INTL_AUDIO_GENERAL_INSTRUCTIONS = [
  'Record directly from this page.',
  'Do not use AI voice enhancement tools or voice changers.',
  'Speak naturally and clearly.',
  'Maximum recording time: 2 minutes per part.',
];

// ---------------------------------------------------------------------------
// Consent
// ---------------------------------------------------------------------------

export const INTL_CONSENT_ITEMS = [
  {
    key: 'consent_data_processing',
    text: 'I authorize TELI to collect, process, and store my personal information for recruitment purposes.',
  },
  {
    key: 'consent_recorded_interviews',
    text: 'I agree to participate in recorded interviews as part of the recruitment process.',
  },
  {
    key: 'consent_accuracy',
    text: 'I certify that all information provided in this application is true, complete, and accurate.',
  },
] as const;

export type IntlConsentKey = (typeof INTL_CONSENT_ITEMS)[number]['key'];

// ---------------------------------------------------------------------------
// Reference number
// ---------------------------------------------------------------------------

/**
 * Derived from the APPLICATION id (not candidate id) so candidate-link and
 * open-intake submissions share one collision-free numbering space.
 */
export function buildIntlReferenceNumber(applicationId: number, year: number): string {
  return `PH-${year}-${String(applicationId).padStart(5, '0')}`;
}
